import { safeMap, SafePromise } from '@benzinga/safe-await';
import { Authentication, AccessType, Permission, RefreshResponse, ProfileData, LayoutListItem } from './user';
import { IngressSubscription, ingestSubscriptions } from './user-subscription-ingress';

export interface SavedLayout {
  data: unknown;
  date_created?: string;
  date_updated?: string;
  ip_address: string;
  name: string;
  user_agent: string;
  uuid: string;
}

export interface Messages {
  display_type: 'modal' | 'banner';
  duration: 'timeout' | 'session';
  html: string;
  properties: {
    width: string;
    height: string;
  };
  required: boolean;
  timeout?: number;
  url: string;
  uuid: string;
}

export interface IngressUser {
  access_type: AccessType;
  benzinga_uid: number;
  display_name: string;
  email: string;
  email_verified: boolean;
  first_name: string;
  human_verified: boolean;
  id: number;
  last_name: string;
  messages: Messages[];
  permissions: Permission[];
  subscriptions?: IngressSubscription[] | null;
  phone_info: null;
  phone_number: string;
  profile_image: string;
  sms_verified: boolean;
  tradeit_token: string;
  trial_end_date: string | null;
  uuid: string;
  hubspot_id: string;
  layout_list?: LayoutListItem[];
  layouts: null;
  coral_jwt: string;
  profile_data?: ProfileData;
  intercom_user_hmac: string;
}

export interface IngressAuthentication {
  csrf_token: string;
  exp: number;
  key: string;
  user: IngressUser;
}

export interface IngressRefreshResponse {
  access_type: AccessType;
  exp: number;
  permissions: Permission[];
}

export const ingestRefreshResponse = async (
  incomingRefreshResponse: SafePromise<IngressRefreshResponse>,
): SafePromise<RefreshResponse> =>
  safeMap(incomingRefreshResponse, refresh => ({
    accessType: refresh.access_type,
    exp: refresh.exp,
    permissions: refresh.permissions,
  }));

export const ingestAuthentication = async (
  incomingAuthentication: SafePromise<IngressAuthentication>,
): SafePromise<Authentication> =>
  safeMap(incomingAuthentication, authentication => ({
    csrfToken: authentication.csrf_token,
    exp: authentication.exp,
    key: authentication.key,
    user: {
      accessType: authentication.user.access_type,
      benzingaUid: authentication.user.benzinga_uid,
      coralJWT: authentication.user.coral_jwt,
      displayName: authentication.user.display_name,
      email: authentication.user.email,
      emailVerified: authentication.user.email_verified,
      firstName: authentication.user.first_name,
      hubspot_id: authentication.user.hubspot_id,
      humanVerified: authentication.user.human_verified,
      id: authentication.user.id,
      intercom_user_hmac: authentication.user.intercom_user_hmac,
      lastName: authentication.user.last_name,
      layout_list: authentication.user.layout_list ?? [],
      layouts: authentication.user.layouts,
      messages: authentication.user.messages?.map(a => ({ ...a, displayType: a.display_type })) ?? [],
      permissions: authentication.user.permissions,
      phoneInfo: authentication.user.phone_info,
      phoneNumber: authentication.user.phone_number,
      profileData: authentication.user.profile_data,
      profileImage: authentication.user.profile_image,
      smsVerified: authentication.user.sms_verified,
      subscriptions: ingestSubscriptions(authentication.user.subscriptions ?? []),
      tradeitToken: authentication.user.tradeit_token,
      trialEndDate: authentication.user?.trial_end_date ?? null,
      uuid: authentication.user.uuid,
    },
  }));

export interface IngressSmsResponse {
  sms_verified?: boolean;
}

export const ingestSmsResponse = async (incomingSmsResponse: SafePromise<IngressSmsResponse>): SafePromise<boolean> =>
  safeMap(incomingSmsResponse, sms => sms.sms_verified ?? false);

export interface IngressEmailResponse {
  email_verified?: boolean;
}

export const ingestEmailResponse = async (
  incomingEmailResponse: SafePromise<IngressEmailResponse>,
): SafePromise<boolean> => safeMap(incomingEmailResponse, email => email.email_verified ?? false);

export interface IngressHumanResponse {
  human_verified?: boolean;
}

export const ingestHumanResponse = async (
  incomingHumanResponse: SafePromise<IngressHumanResponse>,
): SafePromise<boolean> => safeMap(incomingHumanResponse, sms => sms.human_verified ?? false);
